import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import api from '../../utils/functions/api';

const API = `${process.env.REACT_APP_API_URL}/ai-followup`;

export const SEVERITY_META = {
  high: { label: 'สำคัญมาก', bg: '#fee2e2', color: '#991b1b' },
  warn: { label: 'ควรระวัง', bg: '#ffedd5', color: '#9a3412' },
  info: { label: 'ข้อมูล', bg: '#e0f2fe', color: '#075985' },
};
const PRIORITY_LABEL = { high: 'สูง', medium: 'กลาง', low: 'ต่ำ' };
const STATUS_LABEL = {
  pending_review: 'รอหัวหน้าตรวจคำถาม',
  open: 'รอตอบ',
  answered: 'ตอบแล้ว (รอตรวจสอบ)',
  waiting_evidence: 'รอหลักฐาน',
  evidence_verified: 'มีหลักฐานยืนยันแล้ว',
  resolved: 'แก้ไขแล้ว (รออนุมัติปิด)',
};
const EVENT_LABEL = { opened: 'พบครั้งแรก', seen: 'ยังพบอยู่', severity_changed: 'ระดับเปลี่ยน', reopened: 'กลับมาเป็นอีก', closed: 'ปิด' };

const fmt = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('th-TH', { maximumFractionDigits: 2 }));

// ตัวเลขสำคัญ: [คีย์, ชื่อไทย] — ชื่อตรงกับป้ายใน T-1
const KEY_METRICS = [
  ['po_net', 'PO (ไม่รวม VAT)'],
  ['total_budget', 'งบ BG รวม'],
  ['paid', 'จ่ายจริงแล้ว'],
  ['expected_unpaid', 'ขอเบิก/อนุมัติ รอจ่าย'],
  ['total_payable', 'สรุปต้องจ่ายอีกทั้งหมด'],
  ['bg_remain_now', 'BG Remain now'],
  ['profit', 'กำไรคาดการณ์'],
  ['received', 'รับเงินจากลูกค้าแล้ว'],
  ['bills_outstanding', 'จ่ายแล้วแต่ยังไม่ได้รับบิลคืน'],
];

export function openProjectInErp(projectId) {
  window.open(`/project-popup/${projectId}`, `project-popup-${projectId}`, 'width=1280,height=860');
}

function Section({ title, children }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      {children}
    </Box>
  );
}

// รายงานผู้บริหาร 1 โครงการ — รวมตัวเลขสำคัญ สรุป/ข้อพบของ AI ประเด็นต่อเนื่อง คำถามค้าง และสิ่งที่เปลี่ยนจากวันก่อน
function ExecutiveProjectReport({ projectId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let alive = true;
    setLoading(true);
    setData(null);
    api.get(`${API}/project-report/${projectId}`)
      .then((res) => alive && setData(res.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, projectId]);

  const m = data?.metrics;
  const a = data?.analysis;
  const ch = data?.changes;

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontWeight: 700 }}>รายงานผู้บริหาร: {data?.project?.label || '...'}</Typography>
        {data && (
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
            <Chip size="small" label={`ข้อมูลวันที่ ${data.data_date || '-'}`} />
            {a && <Chip size="small" label={`AI วิเคราะห์ ${a.date} ฉบับที่ ${a.version} · ${a.provider} ${a.model}`} />}
          </Stack>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <CircularProgress size={24} />}
        {!loading && !data && <Typography color="text.secondary">ไม่พบข้อมูลโครงการนี้ในระบบ AI ติดตามโครงการ</Typography>}
        {data && !m && <Typography color="text.secondary">โครงการนี้ยังไม่มีข้อมูลที่ระบบเก็บไว้ (ไม่อยู่ในขอบเขตติดตาม)</Typography>}
        {m && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              {KEY_METRICS.map(([k, label]) => (
                <Paper key={k} variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography sx={{ fontWeight: 700, color: (k === 'bg_remain_now' || k === 'profit') && m[k] < 0 ? '#b91c1c' : 'inherit' }}>
                    {fmt(m[k])}{k === 'profit' ? ` (${m.profit_pct}%)` : ''}
                  </Typography>
                </Paper>
              ))}
            </Box>

            <Section title="สรุปจาก AI">
              {a ? (
                <>
                  <Typography variant="body2">{a.summary || '(AI ไม่ได้ให้สรุป)'}</Typography>
                  {a.findings.length === 0 && <Typography variant="caption" color="text.secondary">AI ไม่พบประเด็นที่ต้องแจ้งในการวิเคราะห์ครั้งล่าสุด</Typography>}
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {a.findings.map((f) => {
                      const sev = SEVERITY_META[f.severity] || SEVERITY_META.info;
                      return (
                        <Paper key={`${f.issue_key}-${f.title}`} variant="outlined" sx={{ p: 1, borderLeft: `4px solid ${sev.color}` }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip size="small" label={sev.label} sx={{ bgcolor: sev.bg, color: sev.color, fontWeight: 600 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.title}</Typography>
                            {f.unverified_numbers?.length > 0 && (
                              <Tooltip title={`ตัวเลขที่ระบบหาที่มาในข้อมูลจริงไม่เจอ: ${f.unverified_numbers.map(fmt).join(', ')}`}>
                                <Chip size="small" label="ตัวเลขในข้อความยังตรวจไม่ได้" sx={{ bgcolor: '#fef9c3', color: '#854d0e' }} />
                              </Tooltip>
                            )}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">{f.detail}</Typography>
                        </Paper>
                      );
                    })}
                  </Stack>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">ยังไม่มีผลวิเคราะห์จาก AI ของโครงการนี้ (ตัวเลขด้านบนมาจากข้อมูลจริงในระบบ)</Typography>
              )}
            </Section>

            <Section title={`ประเด็นที่ติดตามอยู่ (${data.issues.length})`}>
              {data.issues.length === 0 && <Typography variant="body2" color="text.secondary">ไม่มี</Typography>}
              <Stack spacing={0.5}>
                {data.issues.map((i) => {
                  const sev = SEVERITY_META[i.severity] || SEVERITY_META.info;
                  return (
                    <Box key={i.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={sev.label} sx={{ bgcolor: sev.bg, color: sev.color }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{`พบครั้งแรก ${i.first_seen_date} · ล่าสุด ${i.last_seen_date}`}</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
                        {i.events.map((e) => `${e.date} ${EVENT_LABEL[e.type] || e.type}`).join(' → ')}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Section>

            <Section title={`คำถามที่ยังไม่ปิด (${data.questions.length})`}>
              {data.questions.length === 0 && <Typography variant="body2" color="text.secondary">ไม่มี</Typography>}
              <Stack spacing={0.5}>
                {data.questions.map((q) => (
                  <Box key={q.id}>
                    <Typography variant="body2">{q.text}</Typography>
                    <Typography variant="caption" sx={{ color: q.overdue ? '#b91c1c' : 'text.secondary', fontWeight: q.overdue ? 700 : 400 }}>
                      {`${STATUS_LABEL[q.status] || q.status} · ความสำคัญ ${PRIORITY_LABEL[q.priority] || '-'} · ${q.due_date ? `กำหนดตอบ ${q.due_date}` : 'ไม่มีกำหนด'}${q.overdue ? ' (เกินกำหนด)' : ''}${q.escalated ? ' · ส่งต่อผู้บริหารแล้ว' : ''}${q.source ? ` · ที่มา: ${q.source}` : ''}`}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Section>

            <Divider sx={{ mt: 2 }} />
            <Section title="สิ่งที่เปลี่ยนจากวันก่อน">
              {!ch.deltas.length && !ch.status_changes.length && !ch.new_expenses.length && (
                <Typography variant="body2" color="text.secondary">ตัวเลขและสถานะไม่เปลี่ยน</Typography>
              )}
              {ch.deltas.map((d) => (
                <Typography key={d.key} variant="body2">{`${d.label}: ${fmt(d.from)} → ${fmt(d.to)} (${d.delta > 0 ? '+' : ''}${fmt(d.delta)})`}</Typography>
              ))}
              {ch.status_changes.map((s) => (
                <Typography key={s.field} variant="body2">{`สถานะ ${s.field}: ${s.from ?? '-'} → ${s.to ?? '-'}`}</Typography>
              ))}
              {ch.new_expenses.map((e) => (
                <Typography key={e.id} variant="body2">{`รายการเบิกใหม่: ${e.name || '(ไม่มีชื่อ)'} ${fmt(e.price)} บาท [${e.status}]`}</Typography>
              ))}
            </Section>
            {(ch.anomalies.length > 0 || ch.notes_conflicts.length > 0) && (
              <Section title="ข้อสังเกตจากระบบ">
                {ch.anomalies.map((x) => (
                  <Typography key={x.title} variant="body2">{`• ${x.title}${x.detail ? ` — ${x.detail}` : ''}`}</Typography>
                ))}
                {ch.notes_conflicts.map((x) => (
                  <Typography key={x.title} variant="body2" color="text.secondary">{`• บันทึกอาจขัดกับข้อมูล: ${x.title}`}</Typography>
                ))}
              </Section>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {projectId && <Button onClick={() => openProjectInErp(projectId)}>เปิดโครงการใน ERP</Button>}
        <Button onClick={onClose} autoFocus>ปิดหน้าต่าง</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExecutiveProjectReport;
